import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import PersonIcon from '@mui/icons-material/Person';
import PlaceIcon from '@mui/icons-material/Place';
import { CalendarIcon } from '@mui/x-date-pickers/icons';
import { Details, ManageSearch, Tag } from '@mui/icons-material';
import { DirectorChairIcon } from '../CustomSVGIcons/DirectorChairIcon';
import { DetailsIcon } from '../CustomSVGIcons/DetailsIcon';

interface Icon {
    icon: React.ReactNode; // The MUI icon component
    parent: string; // Parent category (e.g., 'audition', 'event')
    /** Ensure that this ID matches the id of the form from the server */
    id: string; // Unique identifier for the icon
    field?: string | string[] | { start: string; end: string }[]; // Field(s) associated with this icon
    label: string; // Label for the icon
    required?: boolean; // Whether this icon's field is required
    media_support?: string[]; // Supported media types (optional)
    alternateField?: string[]; // Another field that can mark this as completed
}

interface IconGroup {
    label: string; // Label for the icon group
    icons: Icon[]; // Array of icons in the group
}

interface DetailsMenuCategory {
    mainLabel: string; // Main label for the category
    iconGroups: IconGroup[]; // Array of icon groups for the category
}

interface DetailsMenu {
    audition: DetailsMenuCategory;
    event: DetailsMenuCategory;
    campaign: DetailsMenuCategory;
    thought: DetailsMenuCategory;
}

export type { Icon, IconGroup, DetailsMenuCategory, DetailsMenu };

export const detailsMenu: DetailsMenu = {
    audition: {
        mainLabel: 'Add Audition details',
        iconGroups: [
            {
                label: 'audition',
                icons: [
                    {
                        icon: <AddPhotoAlternateIcon />,
                        parent: 'audition',
                        id: 'add_image',
                        field: 'imageCover',
                        label: 'Add cover image',
                        required: true,
                        media_support: ['jpeg', 'png', 'webp'],
                        alternateField: ['selectedColor'],
                    },
                    {
                        icon: <DetailsIcon />,
                        parent: 'audition',
                        id: 'add_audition_details',
                        field: [
                            'synopsis',
                            'plot',
                            'production_company',
                            'attachment',
                            'attachment_type',
                            'attachment_description',
                        ],
                        label: 'Add Audition details',
                        required: true,
                    },
                    {
                        icon: <DirectorChairIcon sx={{ height: '20px', width: '20px' }} />,
                        parent: 'audition',
                        id: 'add_role',
                        field: 'role',
                        label: 'Add roles',
                        required: true,
                    },
                    {
                        icon: <PlaceIcon />,
                        parent: 'audition',
                        id: 'add_audition_locations',
                        field: ['talent_location', 'shoot_location', 'work_location'],
                        label: 'Add location details',
                        required: true,
                    },
                    {
                        icon: <CalendarIcon />,
                        parent: 'audition',
                        id: 'add_audition_dates',
                        field: [
                            'job_start_date',
                            'job_end_date',
                            'rehearsal_date',
                            'rehearsal_date',
                            'casting_expiration',
                        ],
                        label: 'Add Audition dates',
                        required: true,
                    },

                    {
                        icon: <Tag />,
                        parent: 'audition',
                        id: 'add_audition_tags',
                        field: [
                            'tags',
                            'audition_type',
                            'submission_report',
                            'required_media',
                            'required_skill',
                            'gig_type',
                        ],
                        label: 'Add audition tags',
                    },
                ],
            },
        ],
    },
    event: {
        mainLabel: 'Add event details',
        iconGroups: [
            {
                label: 'event',
                icons: [
                    {
                        icon: <AddPhotoAlternateIcon />,
                        parent: 'event',
                        id: 'add_image',
                        field: 'feature_image',
                        label: 'Add event cover image',
                        required: true,
                    },
                    {
                        icon: <ManageSearch sx={{ fontSize: 28 }} />,
                        parent: 'event',
                        id: 'add_event_details',
                        field: [
                            'description',
                            'location',
                            'event_start_date',
                            'event_end_date',
                            'with_fee',
                            'fee_structure',
                        ],
                        label: 'Add event details',
                        required: true,
                    },
                    {
                        icon: <PersonIcon />,
                        parent: 'event',
                        id: 'add_host_details',
                        field: ['about_host', 'host_contact'],
                        label: 'Add host details',
                        required: false,
                    },
                    {
                        icon: <Tag />,
                        parent: 'event',
                        id: 'add_event_tags',
                        field: ['tags', 'kind'],
                        label: 'Add event tags',
                        required: true,
                    },
                    {
                        icon: <Details />,
                        parent: 'event',
                        id: 'add_additional_event_details',
                        field: [
                            'requirements',
                            'dress_code',
                            'age_restriction',
                            'max_ticket_purchasable_per_person',
                            'total_tickets',
                            'refund_policy',
                            'faq',
                        ],
                        label: 'Add more details',
                        required: false,
                    },
                ],
            },
        ],
    },
    campaign: {
        mainLabel: 'Request New Campaign',
        iconGroups: [
            {
                label: 'campaign',
                icons: [
                    {
                        icon: <Details />,
                        parent: 'campaign',
                        id: 'create_campaign',
                        field: ['campaign_name', 'campaign_budget', 'campaign_objective'],
                        label: 'Add campaign details',
                        required: true,
                    },
                ],
            },
        ],
    },
    thought: {
        mainLabel: 'Add to your post',
        iconGroups: [
            {
                label: 'thought',
                icons: [
                    {
                        icon: <AddPhotoAlternateIcon />,
                        parent: 'thought',
                        id: 'add_image',
                        field: 'image',
                        label: 'Add image to your thought',
                        required: false,
                    },
                    {
                        icon: <PlaceIcon />,
                        parent: 'thought',
                        id: 'add_location',
                        field: 'location',
                        label: 'Add location to your thought',
                        required: false,
                    },
                    {
                        icon: <PersonIcon />,
                        parent: 'thought',
                        id: 'tag_people',
                        field: 'tags',
                        label: 'Tag people in your thought',
                        required: false,
                    },
                ],
            },
        ],
    },
};
